import { FC } from 'react';
import clsx from 'clsx';
import { useUserDetailsStore } from '@marriott/mi-store-utils';
import { EnrollmentCardContainerProps, EnrollmentCardIcon } from './EnrollmentCardContainer.types';
import { EnrollmentCard } from '../../molecules/EnrollmentCard';
import { Amenity } from '../../molecules/EnrollmentCard/Amenity';

function buttonTarget(openInaNewTab?: boolean): '_blank' | '_self' {
  return openInaNewTab ? '_blank' : '_self';
}

function extractIconsFromProps<Props>(props: Record<string, Props>): EnrollmentCardIcon[] {
  const icons: EnrollmentCardIcon[] = [];
  const pathKeys = Object.keys(props).filter(key => key.startsWith('iconPath_'));

  pathKeys.forEach(pathKey => {
    const index = pathKey.split('_')[1];
    const icon: EnrollmentCardIcon = {
      path: (props[`iconPath_${index}`] as string) || '',
      altText: (props[`iconAltText_${index}`] as string) || '',
    };
    icons.push(icon);
  });

  return icons;
}

export const EnrollmentCardContainerComp: FC<EnrollmentCardContainerProps & { className?: string }> = ({
  className,
  variation,
  header,
  primaryCtaLinkText,
  primaryCtaLink,
  primaryCtaOpenInNewTab,
  secondaryCtaLinkText,
  secondaryCtaLink,
  secondaryCtaOpenInNewTab,
  enrollmentCardDescription,
  altText,
  dynamicMedia,
  fileReferenceImage,
  styleclass,
  ...rest
}) => {
  const isSignedInUser = useUserDetailsStore(state => state.isSignedInUser);
  const loggedInVariant = isSignedInUser || variation === 'logged-in';
  const loggedOutVariant = !loggedInVariant;

  const buttons = [
    {
      buttonCopy: primaryCtaLinkText,
      className: `m-button-m m-button-primary${loggedInVariant && '-inverse'}`,
      href: primaryCtaLink,
      target: buttonTarget(primaryCtaOpenInNewTab),
      ariaLabel: primaryCtaLinkText,
    },
  ];

  const icons = extractIconsFromProps(rest);
  const image = loggedOutVariant
    ? undefined
    : {
        altText,
        dynamicMedia,
        fileReferenceImage,
      };

  if (loggedOutVariant && secondaryCtaLinkText) {
    buttons.push({
      buttonCopy: secondaryCtaLinkText,
      className: 'm-button-m m-button-secondary',
      href: secondaryCtaLink,
      target: buttonTarget(secondaryCtaOpenInNewTab),
      ariaLabel: secondaryCtaLinkText,
    });
  }

  return (
    <EnrollmentCard
      className={clsx(className, styleclass?.includes('inverse') && styleclass)}
      buttons={buttons}
      header={header}
      description={enrollmentCardDescription}
      isLoggedInVariant={loggedInVariant}
      image={image}
    >
      {loggedOutVariant &&
        icons.map(({ path, altText }) => {
          const [iconCategory, iconClass] = path.split(':');
          const iconClassName = iconClass || iconCategory;
          return <Amenity key={path + altText} iconClass={iconClassName} label={altText} />;
        })}
    </EnrollmentCard>
  );
};
